import type {
  GridDimensions,
  GridPosition,
  GridSelectionChange,
  LetterBoard,
  LetterCell,
  LetterClearedCell,
  LetterMovedCell,
} from './types'

export function gridPositionKey(position: GridPosition) {
  return `${position.row}:${position.col}`
}

export function isAdjacentPosition(
  previous: GridPosition,
  next: GridPosition,
  allowDiagonals: boolean,
) {
  const rowDelta = Math.abs(previous.row - next.row)
  const colDelta = Math.abs(previous.col - next.col)
  if (rowDelta === 0 && colDelta === 0) {
    return false
  }

  if (allowDiagonals) {
    return rowDelta <= 1 && colDelta <= 1
  }

  return rowDelta + colDelta === 1
}

export function arePositionsContiguouslyConnected(
  positions: GridPosition[],
  allowDiagonals: boolean,
) {
  if (positions.length <= 1) {
    return true
  }

  const uniquePositions: GridPosition[] = []
  const seen = new Set<string>()

  for (const position of positions) {
    const key = gridPositionKey(position)
    if (seen.has(key)) {
      continue
    }

    seen.add(key)
    uniquePositions.push(position)
  }

  if (uniquePositions.length <= 1) {
    return true
  }

  const visited = new Set<string>()
  const stack = [uniquePositions[0]!]

  while (stack.length > 0) {
    const current = stack.pop()!
    const currentKey = gridPositionKey(current)
    if (visited.has(currentKey)) {
      continue
    }

    visited.add(currentKey)

    for (const candidate of uniquePositions) {
      const candidateKey = gridPositionKey(candidate)
      if (visited.has(candidateKey)) {
        continue
      }

      if (isAdjacentPosition(current, candidate, allowDiagonals)) {
        stack.push(candidate)
      }
    }
  }

  return visited.size === uniquePositions.length
}

export function collapseBoardDownward(
  board: LetterBoard,
  dimensions: GridDimensions,
): {
  board: LetterBoard
  moved: LetterMovedCell[]
} {
  const nextBoard: LetterBoard = Array.from({ length: dimensions.rows }, () =>
    Array.from({ length: dimensions.cols }, () => null),
  )
  const moved: LetterMovedCell[] = []

  for (let col = 0; col < dimensions.cols; col += 1) {
    const survivors: Array<{ cell: LetterCell; row: number }> = []

    for (let row = dimensions.rows - 1; row >= 0; row -= 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      survivors.push({ cell, row })
    }

    let targetRow = dimensions.rows - 1
    for (const survivor of survivors) {
      nextBoard[targetRow]![col] = survivor.cell
      if (survivor.row !== targetRow) {
        moved.push({
          col,
          fromCol: col,
          fromRow: survivor.row,
          id: survivor.cell.id,
          letter: survivor.cell.letter,
          tags: survivor.cell.tags,
          toCol: col,
          toRow: targetRow,
        })
      }
      targetRow -= 1
    }
  }

  return { board: nextBoard, moved }
}

export function rotateLetterBoard(
  board: LetterBoard,
  dimensions: GridDimensions,
  direction: 'left' | 'right',
): LetterBoard {
  const nextBoard: LetterBoard = Array.from({ length: dimensions.cols }, () =>
    Array.from({ length: dimensions.rows }, () => null),
  )

  for (let row = 0; row < dimensions.rows; row += 1) {
    for (let col = 0; col < dimensions.cols; col += 1) {
      const cell = board[row]?.[col]
      if (!cell) {
        continue
      }

      const nextRow = direction === 'right' ? col : dimensions.cols - 1 - col
      const nextCol = direction === 'right' ? dimensions.rows - 1 - row : row
      nextBoard[nextRow]![nextCol] = cell
    }
  }

  return nextBoard
}

export function gridTryBeginSelection(
  board: LetterBoard,
  dimensions: GridDimensions,
  position: GridPosition,
): GridPosition[] | null {
  if (
    position.row < 0 ||
    position.row >= dimensions.rows ||
    position.col < 0 ||
    position.col >= dimensions.cols
  ) {
    return null
  }

  if (!board[position.row]?.[position.col]) {
    return null
  }

  return [position]
}

export function gridUpdateSelection(
  board: LetterBoard,
  dimensions: GridDimensions,
  allowDiagonals: boolean,
  selection: GridPosition[],
  position: GridPosition,
  repeatTileMinGap = 0,
  requireStepAdjacency = true,
  forbidStepAdjacency = false,
): { change: GridSelectionChange; selection: GridPosition[] } {
  if (selection.length === 0) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  const last = selection[selection.length - 1]
  const previous = selection[selection.length - 2]

  if (
    position.row < 0 ||
    position.row >= dimensions.rows ||
    position.col < 0 ||
    position.col >= dimensions.cols
  ) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  if (!board[position.row]?.[position.col]) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  const doublingSameTile =
    last !== undefined &&
    last.row === position.row &&
    last.col === position.col

  if (doublingSameTile) {
    if (repeatTileMinGap > 0) {
      return {
        change: { type: 'ignored' },
        selection,
      }
    }

    return {
      change: { position, type: 'append' },
      selection: [...selection, position],
    }
  }

  if (
    previous &&
    previous.row === position.row &&
    previous.col === position.col &&
    repeatTileMinGap > 1
  ) {
    return {
      change: { position, type: 'backtrack' },
      selection: selection.slice(0, -1),
    }
  }

  let lastVisitedIndex = -1
  for (let index = selection.length - 1; index >= 0; index -= 1) {
    const entry = selection[index]
    if (entry && entry.row === position.row && entry.col === position.col) {
      lastVisitedIndex = index
      break
    }
  }

  if (
    lastVisitedIndex !== -1 &&
    selection.length - lastVisitedIndex - 1 < repeatTileMinGap
  ) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  const isAdjacentStep =
    last !== undefined && isAdjacentPosition(last, position, allowDiagonals)
  const isCardinalAdjacentStep =
    last !== undefined && isAdjacentPosition(last, position, false)

  if (requireStepAdjacency && (!last || !isAdjacentStep)) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  if (forbidStepAdjacency && isCardinalAdjacentStep) {
    return {
      change: { type: 'ignored' },
      selection,
    }
  }

  return {
    change: { position, type: 'append' },
    selection: [...selection, position],
  }
}
